import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled from '@emotion/styled';
import {
  Box,
  Button,
  Card,
  Content,
  CONTENT_TYPE,
  Flex,
  FormField,
  TextInput,
  Select,
  ToolTip,
} from '../../components';
import {
  getCarrier,
  getFeatures,
} from '../../modules/selectors';
import api from '../../utils/services';

const propTypes = {
  features: PropTypes.shape({
    liquidTemplates: PropTypes.bool,
    richText: PropTypes.bool,
    staticHtml: PropTypes.bool,
  }).isRequired,
  onSaveComplete: PropTypes.func,
  contentId: PropTypes.string,
  carrier: PropTypes.shape({
    carrierId: PropTypes.number,
  }).isRequired,
};

const defaultProps = {
  contentId: undefined,
  onSaveComplete: undefined,
};

const OverrideTooltipInlineBlock = styled.div`
  & > span {
    display: block;
    & > span {
      display: block;
    }
  }
`;

class ContentForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      content: {
        name: '',
        type: '',
        value: '',
        carrierId: props.carrier.carrierId,
      },
      loading: false,
      saving: false,
    };
  }

  componentDidMount() {
    const { contentId } = this.props;
    if (contentId) {
      api.getContents(contentId)
        .then(content => this.setState({ content, loading: false }));
    }
  }

  handleSubmit = (event) => {
    event.preventDefault();
    const { contentId, onSaveComplete } = this.props;
    const { content } = this.state;

    this.setState({ saving: true });

    let saveContent;
    if (contentId) {
      saveContent = api.updateContent(content.id, { ...content });
    } else {
      saveContent = api.addContent({ ...content });
    }
    saveContent.then((contentSaved) => {
      this.setState({ saving: false });
      if (onSaveComplete) {
        onSaveComplete(contentSaved);
      }
    });
  }

  handleChange = (event) => {
    const { target: { name, value } } = event;
    this.setState((state) => {
      const newState = {
        content: {
          ...state.content,
          [name]: value,
        },
      };
      if (name === 'type') {
        newState.content.value = ''; // Clear the value since it is not compatible for different types
      }
      return newState;
    });
  }

  handleSelectChange = (name, value) => this.handleChange({ target: { name, value } })

  render() {
    const {
      content,
      loading,
      saving,
    } = this.state;
    const {
      carrier,
      contentId,
      features,
    } = this.props;

    // Trigger dropdown values based on the features enabled.
    const typeDropDownData = [{ text: 'Plain Text', value: CONTENT_TYPE.PLAIN, secondaryText: 'Easy, Fast' }];
    if (features.richText) {
      typeDropDownData.push({ text: 'Rich Text', value: CONTENT_TYPE.QUILL, secondaryText: 'Easy, Powerful' });
    }
    if (features.staticHtml) {
      typeDropDownData.push({ text: 'Static HTML', value: CONTENT_TYPE.HTML, secondaryText: 'Advanced, Fast' });
    }
    if (features.liquidTemplates) {
      typeDropDownData.push({ text: 'Liquid HTML', value: CONTENT_TYPE.LIQUID, secondaryText: 'Advanced, Powerful' });
    }

    // Select requires strings for values
    const scopeDropDownData = [
      { text: 'Carrier', value: JSON.stringify(carrier.carrierId) },
      { text: 'Client', value: JSON.stringify(null) },
    ];

    /* Note the form below is intentionally not wrapped in a <form> element
     * to prevent button from propagating up submit. This can be fixed, by
     * insuring all button rendered by this from have type="button", but an
     * implementation of a future content type my forget to add that.
     */
    return (
      <Card loading={loading}>
        <Card.Block>
          <Flex>
            <Flex.Item grow={2}>
              <FormField
                label="Name"
                input={TextInput}
                placeholder="Name"
                name="name"
                value={content.name}
                onChange={this.handleChange}
              />
            </Flex.Item>
            <Flex.Item grow={1}>
              <FormField
                label="Scope"
                name="carrierId"
                placeholder="Select Scope"
                input={Select}
                data={scopeDropDownData}
                onChange={item => this.handleSelectChange('carrierId', JSON.parse(item.value))}
                selectedItem={
                  scopeDropDownData.find(s => s.value === JSON.stringify(content.carrierId || null))
                }
              />
            </Flex.Item>
            <Flex.Item grow={1}>
              {
                /* Override tooltip span to use display: block
                * to allow the FormField to expand with the Flex
                */
              }
              <OverrideTooltipInlineBlock>
                <ToolTip content="You cannot change a content's type after it has been created" disabled={!contentId} placement="top">
                  <FormField
                    label="Type"
                    name="type"
                    input={Select}
                    placeholder="Select Type"
                    data={typeDropDownData}
                    onChange={item => this.handleSelectChange('type', item.value)}
                    selectedItem={typeDropDownData.find(t => t.value === content.type)}
                    disabled={!!contentId} // disable dropdown during editing.
                  />
                </ToolTip>
              </OverrideTooltipInlineBlock>
            </Flex.Item>
          </Flex>
          <Box marginTop="md">
            {content.type && (
              <Content
                edit
                onChange={value => this.handleChange({ target: { name: 'value', value } })}
                carrierId={content.carrierId}
                type={content.type}
                value={content.value}
              />
            )}
          </Box>
          {/* hide save button until we have enough data supplied. */}
          {content.name && content.type && (
            <Flex paddingTop="1em">
              <Flex.Item marginStart="auto">
                <Button primary loading={saving} onClick={this.handleSubmit}>Save</Button>
              </Flex.Item>
            </Flex>
          )}
        </Card.Block>
      </Card>
    );
  }
}

function mapStateToProps(state) {
  return {
    features: getFeatures(state),
    carrier: getCarrier(state),
  };
}
ContentForm.propTypes = propTypes;
ContentForm.defaultProps = defaultProps;
export default withRouter(connect(mapStateToProps)(ContentForm));
